import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { ProfileResponse } from '../../core/models/profile.model';
import { NavigationService } from '../../core/services/navigation.service';
import { HttpService } from '../../core/services/http.service';
import { TokenService } from '../../core/services/token.service';
import { ApiHandlerService } from '../../core/services/api-handler/api-handler.service';
import { ApiUrls } from '../../core/utilities/APIUris';
import { LoginRequestInput, LoginResponse, OTPRequestInput, OTPRequestResponse, OTPVerifyRequestInput, RefreshTokenResponse, RegisterUserRequestInput, ResetPasswordRequest } from '../../core/models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private userSubject: BehaviorSubject<any>;
  public user: Observable<any>;

  public profileSubject = new BehaviorSubject<any>(null);
  public profile: Observable<ProfileResponse> = this.profileSubject.asObservable();
  public loggedInUserId: string = '';
  public loggedInUserIdOrgId: string = '';
  public isOnboardingCompleted: boolean = false;
  public organization_profile:any

  public permissionList: any = {};
  constructor(
    private navigationService: NavigationService,
    private http: HttpService,
    private tokenService: TokenService,
    private ApiHandlerService: ApiHandlerService
  ) {
    this.userSubject = new BehaviorSubject(null);
    this.user = this.userSubject.asObservable();
    this.profileSubject.subscribe((result: ProfileResponse) => {
      if (result) {
        this.loggedInUserId = result?.id;
        if (result && result.organization_users) {
          this.isOnboardingCompleted = result.organization_users && Object.keys(result.organization_users).length > 0 ? true : false;
          this.organization_profile = result;
          this.loggedInUserIdOrgId = result.organization_users.org_id;
        }
        if (result.auth_items) {
          this.permissionList = result.auth_items;
        }
      }
    });
  }

  public get userValue() {
    return this.userSubject.value;
  }

  ngOnDestroy(): void {
    throw new Error('Method not implemented.');
  }

  getPublicConfig() {
    // return this.http.get(ApiUrls.publicConfig);
    return this.ApiHandlerService.doGet<any>(ApiUrls.publicConfig,false,false)
  }

  forgotPassword(requestPayload: OTPRequestInput): Observable<OTPRequestResponse> {
    return this.ApiHandlerService.doPost<OTPRequestResponse>(ApiUrls.forgotPassword, requestPayload, false, false);
  }


  verifyOTP(requestPayload: OTPVerifyRequestInput) {
    return this.http.post(ApiUrls.verifyOtpRequest, requestPayload);
  }

  registerUser(requestPayload: RegisterUserRequestInput): Observable<any> {
    return this.ApiHandlerService.doPost(ApiUrls.registerUser, requestPayload,false, false);
  }

  doLogin(requestPayload: LoginRequestInput,rememberMe: boolean): Observable<LoginResponse> {
    return this.ApiHandlerService.doPost(ApiUrls.applogin, requestPayload, false, false).pipe(
      map((user: any) => {
        user.isRememberMe = rememberMe;
        this.tokenService.storeTokens(user);
        this.userSubject.next(user);
        return this.userValue;
      })
    );
  }

  getMyProfile(): Observable<any> {
    if (this.profileSubject.value) {
      return this.profile; // Return existing profile data
    } else {
      return this.refreshMyProfile();
    }
  }

  getMyProfileId() {
    if (this.profileSubject.value) {
      return this.profileSubject.getValue(); // Return existing profile data
    } else {
      return this.refreshMyProfile();
    }
  }

  refreshMyProfile(): Observable<any> {
    return this.ApiHandlerService.doGet(ApiUrls.getProfile, false, false).pipe(
      tap((profile) => this.profileSubject.next(profile))); //Set the profile value
  }
  // getLanguages(): Observable<any> {
  //   return this.ApiHandlerService.doGet(ApiUrls.getLanguages, false, false);
  // }
  // getLanguageDetail(lang: string|null): Observable<any> {
  //   return this.ApiHandlerService.doGet(`${ApiUrls.getLanguages}/${lang}`, false, false);
  // }

  logout() {
    this.tokenService.removeTokens();
    this.userSubject.next(null);
    this.profileSubject.next(null);
    this.navigationService.navigateTo('/login');
  }

  refreshAccessToken(refresh_token: string): Observable<RefreshTokenResponse> {
    return this.http.post(ApiUrls.getAccessTokenFromRefreshToken, { refresh_token }).pipe(
      map((tokens: LoginResponse) => {
        this.tokenService.storeTokens(tokens);
        this.userSubject.next(tokens);
        return this.userValue;
      })
    );
  }

  forgotPassowrd(requestPayload: ResetPasswordRequest) {
    return this.http.put(ApiUrls.forgotResetPassowrd, requestPayload);
  }

  isUserLoggedIn() {
    return this.tokenService.isLoggedIn();
  }

  // Method to check permission
  checkHasPermission(module: string, permission: string): boolean {
    return this.permissionList?.[module]?.[permission] ?? false;
  }

  checkHasPermissionByRole(module: string): boolean {
    const org_type = this.profileSubject.value?.organization_users?.org_details?.org_type;
    if(org_type === 'SUPER_ADMIN'){
      return true;
    }else{
      return false;
    }   
  }

  // Login AS ...
  loginAs( org_id: string): Observable<any> {
    return this.http.post<any>(ApiUrls.loginAs, {org_id});
  }

  hasLoginAs(): boolean {
    return !!this.organization_profile?.login_as;
  }
}
